import {
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  inject,
  input,
  output,
} from '@angular/core';
import { Router } from '@angular/router';
import { MeetingAgentRunDto } from '@interfaces/meetings/responses/meeting-insights.response';
import { TranslateService } from '@ngx-translate/core';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import {
  formatarCustoUsd,
  formatarDuracaoMs,
  formatarTokens,
  rotuloDaSituacaoDoAgente,
  rotuloDoAgente,
} from '@features/dashboard/meetings/pages/meeting-detail/meeting-insights-state';

/** Rota da lista de sessões do back office, que abre a sessão pelo `?sessionId=`. */
const SESSOES_DE_AGENTE = '/dashboard/management/back-office/agent-sessions';

/**
 * Painel "Sessões do Cog" do detalhe da reunião (PBI 3225, adendo de 02/10/2026): só para o time
 * interno, uma linha por agente (Vínculos e Resumo) com situação, erro, tentativas, modelo, tokens,
 * custo em US$, duração, chamadas de tool e versões, para inspecionar o que o Cog fez. **Abrir
 * sessão** abre a conversa do agente no back office (Sessões de Agente) numa aba nova; falhou, o
 * **Gerar de novo** fica aqui também.
 *
 * É o único lugar da tela de reunião com custo de IA: o resto (resumo, vínculos) nunca mostra.
 */
@Component({
  selector: 'app-modal-meeting-agent-runs',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './modal-meeting-agent-runs.component.html',
  styleUrl: './modal-meeting-agent-runs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalMeetingAgentRunsComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  readonly runs = input<MeetingAgentRunDto[] | null>(null);
  /** Libera "Gerar de novo" (o backend manda `canReprocess`). */
  readonly podeGerar = input(false);
  readonly gerando = input(false);
  /** Abrir a sessão pede o back office (ADMIN). */
  readonly podeAbrirSessao = input(false);

  readonly gerar = output<void>();

  protected readonly rotuloDoAgente = rotuloDoAgente;
  protected readonly rotuloDaSituacao = rotuloDaSituacaoDoAgente;
  protected readonly custo = formatarCustoUsd;
  protected readonly duracao = formatarDuracaoMs;
  protected readonly tokens = formatarTokens;

  private readonly router = inject(Router);

  constructor(translateService: TranslateService) {
    super(translateService);
  }

  open(): void {
    this.dialogRef?.open('', '', '', 'Fechar', '', 'meeting-agent-runs', true, false);
  }

  /** Abre a sessão no back office numa aba nova, para não perder a reunião aberta. */
  protected abrirSessao(run: MeetingAgentRunDto): void {
    if (!run.sessionId) return;
    const url = this.router.serializeUrl(
      this.router.createUrlTree([SESSOES_DE_AGENTE], { queryParams: { sessionId: run.sessionId } })
    );
    window.open(url, '_blank', 'noopener');
  }

  protected algumFalhou(): boolean {
    return (this.runs() ?? []).some(r => r.status === 'Failed');
  }
}
